'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useParams, useRouter } from 'next/navigation'
import { ComponentProps, MouseEvent } from 'react'
import { toast } from 'react-toastify'

import { contractQueries } from '@/entities/contract'
import { ACCOUNT_TOAST_CONTAINER_ID } from '@/features/update-account'
import { usePrefetchQuery } from '@/shared/api'
import { useDialog } from '@/shared/lib'
import { AlertDialog, ConfirmDialog, TextButton } from '@/shared/ui'

import { UpdateReceiptInformationDialog } from './dialog'

export const DIALOG = {
  UPDATE_RECEIPT_INFORMATION: 'UPDATE_RECEIPT_INFORMATION',
  CONFIRM_CLOSE: 'CONFIRM_CLOSE',
  UPDATE_COMPLETE: 'UPDATE_COMPLETE',
} as const

type UpdateReceiptInformationDialog = keyof typeof DIALOG

type Props = ComponentProps<typeof TextButton> & {
  contractId?: number
}

export const UpdateReceiptInformationButton = ({
  contractId: contractIdProp,
  onClick,
  children,
  ...restProps
}: Props) => {
  const router = useRouter()

  const params = useParams()
  const contractId = (() => {
    if (contractIdProp) {
      return contractIdProp
    }

    return Number(params?.contractId)
  })()

  const { isOpen, open, close, closeAll } = useDialog()
  const queryClient = useQueryClient()

  const { prefetch } = usePrefetchQuery()

  const closeDialog = (key: UpdateReceiptInformationDialog) => () => {
    close(key)
  }

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!contractId) {
      return
    }

    const { success } = await prefetch(
      contractQueries.guestReceiptInformation({ contractId }),
    )

    if (!success) {
      return
    }

    onClick?.(event)
    open(DIALOG.UPDATE_RECEIPT_INFORMATION)
  }

  const handleUpdateDialogClose = () => {
    toast.dismiss({ containerId: ACCOUNT_TOAST_CONTAINER_ID })
    open(DIALOG.CONFIRM_CLOSE)
  }

  const handleUpdateSuccess = () => {
    open(DIALOG.UPDATE_COMPLETE, { closeOnOpen: true })
  }

  const handleCloseDialogConfirm = () => {
    closeAll()
  }

  const handleUpdateCompleteDialogClose = async () => {
    close(DIALOG.UPDATE_COMPLETE)

    await queryClient.invalidateQueries({
      queryKey: contractQueries.guestMasterContracts({}).queryKey,
    })
    await queryClient.invalidateQueries({
      queryKey: contractQueries.guestNotifications().queryKey,
    })

    router.refresh()
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  return (
    <>
      <TextButton
        size="small"
        weight="normal"
        onClick={handleClick}
        {...restProps}
      >
        {children}
      </TextButton>

      <UpdateReceiptInformationDialog
        isOpen={isOpen(DIALOG.UPDATE_RECEIPT_INFORMATION)}
        onOpenChange={closeDialog(DIALOG.UPDATE_RECEIPT_INFORMATION)}
        contractId={contractId}
        onSuccess={handleUpdateSuccess}
        onClose={handleUpdateDialogClose}
      />

      <ConfirmDialog
        isOpen={isOpen(DIALOG.CONFIRM_CLOSE)}
        onOpenChange={closeDialog(DIALOG.CONFIRM_CLOSE)}
        title="입력을 종료하시겠습니까?"
        content={`입력 종료 시\n변경한 내용이 저장되지 않습니다.`}
        cancelText="종료하기"
        confirmText="계속 입력하기"
        onCancel={handleCloseDialogConfirm}
        onConfirm={closeDialog(DIALOG.CONFIRM_CLOSE)}
        preventOutsideClick
      />

      <AlertDialog
        isOpen={isOpen(DIALOG.UPDATE_COMPLETE)}
        onOpenChange={closeDialog(DIALOG.UPDATE_COMPLETE)}
        content="정보가 저장되었습니다."
        onConfirm={handleUpdateCompleteDialogClose}
        preventOutsideClick
      />
    </>
  )
}
